import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import { KV, KVRow } from '../components/KV';
import DataTable from '../components/DataTable';
import {
  fmt_dt,
  message_status_color,
  recap_from_messages,
  recipients_for,
  safe_http_url,
  status_color,
  verification_meta,
} from './helpers';
import Badge from './Badge';
import type {
  BookingDetailResponse,
  MessageChannel,
  ResendResponse,
  SentMessage,
} from './types';

interface AppConfig {
  email_sending_enabled?: boolean;
  sms_sending_enabled?: boolean;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ marginBottom: 8 }}>{title}</h3>
      {children}
    </div>
  );
}

export default function BookingDrawer({
  booking_id,
  on_close,
}: {
  booking_id: string;
  on_close: () => void;
}) {
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['booking', booking_id],
    queryFn: () =>
      api.get<BookingDetailResponse>(`/api/captomi-office/bookings/${booking_id}`),
  });

  // Send toggles gate the re-send control (spec §4.2). App-level config, not per-user.
  const configQuery = useQuery({
    queryKey: ['app_config', 'captomi_office'],
    queryFn: () =>
      api.get<{ config: AppConfig | null }>('/api/apps/captomi_office/config'),
  });
  const email_enabled = configQuery.data?.config?.email_sending_enabled ?? false;
  const sms_enabled = configQuery.data?.config?.sms_sending_enabled ?? false;

  // Close on Escape.
  useEffect(() => {
    const on_key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') on_close();
    };
    window.addEventListener('keydown', on_key);
    return () => window.removeEventListener('keydown', on_key);
  }, [on_close]);

  // Channel selection for re-send, defaulting to whatever is enabled.
  const [sel, set_sel] = useState<{ email: boolean; sms: boolean }>({
    email: false,
    sms: false,
  });
  const [confirming, set_confirming] = useState(false);
  useEffect(() => {
    set_sel({ email: email_enabled, sms: sms_enabled });
  }, [email_enabled, sms_enabled]);

  const messages: SentMessage[] = data?.messages ?? [];
  const recap = useMemo(() => recap_from_messages(messages), [messages]);

  const selected_channels = useMemo<MessageChannel[]>(() => {
    const out: MessageChannel[] = [];
    if (sel.email) out.push('email');
    if (sel.sms) out.push('sms');
    return out;
  }, [sel]);

  const resend_recipients = useMemo(
    () => recipients_for(messages, selected_channels),
    [messages, selected_channels],
  );

  const resend = useMutation({
    mutationFn: () =>
      api.post<ResendResponse>(`/api/captomi-office/bookings/${booking_id}/resend`, {
        channels: selected_channels,
      }),
    onSuccess: () => {
      set_confirming(false);
      // Replays write new sent_messages rows — refetch so history reflects them.
      qc.invalidateQueries({ queryKey: ['booking', booking_id] });
    },
  });

  const [cancel_confirming, set_cancel_confirming] = useState(false);
  const cancel = useMutation({
    mutationFn: () =>
      api.post<{ booking_id: string; status: string; already_cancelled: boolean }>(
        `/api/captomi-office/bookings/${booking_id}/cancel`,
        {},
      ),
    onSuccess: () => {
      set_cancel_confirming(false);
      // Status flips to CANCELLED — refetch the drawer AND the list (key ['bookings', ...])
      // so the row reflects it without a manual page refresh.
      qc.invalidateQueries({ queryKey: ['booking', booking_id] });
      qc.invalidateQueries({ queryKey: ['bookings'] });
    },
  });

  const both_off = !email_enabled && !sms_enabled;
  const can_resend = selected_channels.length > 0 && !both_off;
  const resend_disabled_reason = both_off
    ? 'Email and SMS sending are both disabled in captomi-office app config.'
    : selected_channels.length === 0
      ? 'Select at least one enabled channel.'
      : '';

  const b = data?.booking;
  const verdict = verification_meta(b?.client_address_verification);

  return (
    <>
      {/* backdrop */}
      <div
        onClick={on_close}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 26, 20, 0.35)',
          zIndex: 40,
        }}
      />
      {/* panel */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(720px, 92vw)',
          background: 'var(--bg)',
          borderLeft: '1px solid var(--line)',
          boxShadow: '-12px 0 32px rgba(31, 93, 58, 0.12)',
          zIndex: 41,
          overflowY: 'auto',
          padding: 24,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            {b ? (
              <>
                <h2 style={{ margin: 0 }}>{b.business_name ?? b.tenant_id}</h2>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  <Badge color={status_color(b.status)}>{b.status}</Badge>
                  {b.booking_kind && (
                    <code style={{ fontSize: 12 }}>{b.booking_kind}</code>
                  )}
                  <span className="muted" style={{ fontSize: 13 }}>
                    {fmt_dt(b.timestamp)}
                  </span>
                </div>
              </>
            ) : (
              <h2 style={{ margin: 0 }}>Booking</h2>
            )}
          </div>
          <button className="secondary" onClick={on_close} style={{ padding: '4px 12px' }}>
            Close
          </button>
        </div>

        {isLoading && <p style={{ marginTop: 24 }}>Loading booking…</p>}
        {error && (
          <p className="error" style={{ marginTop: 24 }}>
            {error instanceof ApiError && error.status === 404
              ? 'Booking not found.'
              : `Failed to load: ${error instanceof ApiError ? error.reason : String(error)}`}
          </p>
        )}

        {data && b && (
          <>
            <Section title="Details">
              <KV>
                <KVRow label="Booking ID" value={<code>{b.booking_id}</code>} />
                <KVRow
                  label="Tenant"
                  value={
                    <Link to={`/tenants/${b.tenant_id}`}>
                      <code>{b.tenant_id}</code>
                    </Link>
                  }
                />
                <KVRow label="User ID" value={<code>{b.user_id}</code>} />
                {b.client_id && <KVRow label="Client ID" value={<code>{b.client_id}</code>} />}
                <KVRow label="Booked for" value={fmt_dt(b.timestamp)} />
                {b.client_name && <KVRow label="Client name" value={b.client_name} />}
                {b.client_phone && <KVRow label="Client phone" value={<code>{b.client_phone}</code>} />}
                {b.prospect_email && <KVRow label="Prospect email" value={b.prospect_email} />}
                {b.prospect_company && <KVRow label="Prospect company" value={b.prospect_company} />}
                {b.project_type && <KVRow label="Project type" value={b.project_type} />}
                {b.client_callback_window && (
                  <KVRow label="Callback window" value={b.client_callback_window} />
                )}
                <KVRow label="Email only" value={b.client_email_only ? 'yes' : 'no'} />
                {b.confirmation_deadline && (
                  <KVRow label="Confirmation deadline" value={fmt_dt(b.confirmation_deadline)} />
                )}
                <KVRow label="Created" value={fmt_dt(b.created_at)} />
                <KVRow label="Conversation" value={<code>{b.conversation_id}</code>} />
              </KV>
            </Section>

            {(b.client_address || b.client_address_raw || b.client_address_verification) && (
              <Section title="Address validation">
                <div style={{ marginBottom: 8 }}>
                  <Badge color={verdict.color}>{verdict.label}</Badge>
                </div>
                {b.client_address_verification === 'corrected' ? (
                  <KV>
                    <KVRow
                      label="Raw (as heard)"
                      value={
                        <span style={{ textDecoration: 'line-through', color: 'var(--muted)' }}>
                          {b.client_address_raw ?? '—'}
                        </span>
                      }
                    />
                    <KVRow
                      label="Corrected"
                      value={<strong>{b.client_address ?? '—'}</strong>}
                    />
                  </KV>
                ) : (
                  <KV>
                    <KVRow label="Address" value={b.client_address ?? b.client_address_raw ?? '—'} />
                    {b.client_address_raw &&
                      b.client_address_raw !== b.client_address && (
                        <KVRow label="Raw (as heard)" value={b.client_address_raw} />
                      )}
                  </KV>
                )}
                {verdict.warn && b.client_address_verification !== 'corrected' && (
                  <p className="error" style={{ marginTop: 8 }}>
                    {b.client_address_verification === 'ambiguous'
                      ? 'Address was ambiguous — confirm with the homeowner before the visit.'
                      : 'Address could not be found — confirm with the homeowner before the visit.'}
                  </p>
                )}
                {b.client_address_geocode && (
                  <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
                    Geocode: {b.client_address_geocode.lat}, {b.client_address_geocode.lng}
                    {b.client_address_geocode.place_id
                      ? ` · ${b.client_address_geocode.place_id}`
                      : ''}
                  </p>
                )}
              </Section>
            )}

            <Section title="Call recap">
              {/* Prefer the notes persisted on the booking (the receptionist's pre-visit
                  briefing); fall back to the recap parsed from the confirmation email. */}
              {(b.notes ?? recap) ? (
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    margin: 0,
                    background: 'var(--green-50)',
                    border: '1px solid var(--line)',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  {b.notes ?? recap}
                </pre>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  No recap captured for this booking.
                </p>
              )}
            </Section>

            <Section title="Transcript">
              {(() => {
                const recording = safe_http_url(data.recording_url);
                return recording ? (
                  <div style={{ marginBottom: 12 }}>
                    {/* Inline player. The signed URL is short-lived (~15 min) but the
                        drawer re-fetches detail on open, so it's fresh per view. */}
                    <audio controls preload="none" src={recording} style={{ width: '100%' }}>
                      <a href={recording} target="_blank" rel="noopener noreferrer">
                        ▶ Recording
                      </a>
                    </audio>
                    <p style={{ margin: '4px 0 0' }}>
                      <a
                        href={recording}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="muted"
                        style={{ fontSize: 13 }}
                      >
                        Open / download recording
                      </a>
                    </p>
                  </div>
                ) : null;
              })()}
              {data.transcript ? (
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 13,
                    margin: 0,
                    maxHeight: 360,
                    overflowY: 'auto',
                    background: '#0f1a14',
                    color: '#e6f0ea',
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  {data.transcript}
                </pre>
              ) : (
                <p className="muted" style={{ margin: 0 }}>
                  No transcript available.
                </p>
              )}
            </Section>

            <Section title={`Message history (${messages.length})`}>
              {messages.length === 0 ? (
                <p className="muted" style={{ margin: 0 }}>
                  No messages recorded for this booking.
                </p>
              ) : (
                <DataTable
                  rows={messages}
                  columns={[
                    { header: 'When', cell: (m: SentMessage) => fmt_dt(m.created_at), width: '160px' },
                    { header: 'Channel', cell: (m: SentMessage) => m.channel },
                    { header: 'Audience', cell: (m: SentMessage) => m.audience },
                    {
                      header: 'To',
                      cell: (m: SentMessage) => (
                        <span style={{ fontSize: 13 }}>{(m.to ?? []).join(', ')}</span>
                      ),
                    },
                    {
                      header: 'Status',
                      cell: (m: SentMessage) => (
                        <Badge color={message_status_color(m.status)}>{m.status}</Badge>
                      ),
                    },
                    {
                      header: 'Body',
                      cell: (m: SentMessage) => (
                        <details>
                          <summary style={{ cursor: 'pointer', fontSize: 13 }}>
                            {m.subject ?? 'view'}
                          </summary>
                          <pre
                            style={{
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'inherit',
                              fontSize: 13,
                              marginTop: 8,
                            }}
                          >
                            {m.body}
                            {m.provider_error ? `\n\n[error] ${m.provider_error}` : ''}
                          </pre>
                        </details>
                      ),
                    },
                  ]}
                />
              )}
            </Section>

            <Section title="Re-send confirmation">
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <label
                  style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}
                  title={email_enabled ? '' : 'Outbound email is disabled in app config.'}
                >
                  <input
                    type="checkbox"
                    checked={sel.email}
                    disabled={!email_enabled}
                    onChange={(e) => set_sel((s) => ({ ...s, email: e.target.checked }))}
                  />
                  Email{email_enabled ? '' : ' (disabled)'}
                </label>
                <label
                  style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}
                  title={sms_enabled ? '' : 'Outbound SMS is disabled in app config.'}
                >
                  <input
                    type="checkbox"
                    checked={sel.sms}
                    disabled={!sms_enabled}
                    onChange={(e) => set_sel((s) => ({ ...s, sms: e.target.checked }))}
                  />
                  SMS{sms_enabled ? '' : ' (disabled)'}
                </label>
              </div>

              {!confirming ? (
                <div style={{ marginTop: 12 }}>
                  <button
                    onClick={() => set_confirming(true)}
                    disabled={!can_resend || resend.isPending}
                    title={resend_disabled_reason}
                  >
                    Re-send confirmation
                  </button>
                </div>
              ) : (
                <div
                  className="card"
                  style={{ marginTop: 12, padding: 16, background: 'var(--green-50)' }}
                >
                  <p style={{ marginTop: 0 }}>
                    Re-send confirmation to{' '}
                    <strong>
                      {resend_recipients.length ? resend_recipients.join(', ') : 'the recorded recipients'}
                    </strong>
                    ?
                  </p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => resend.mutate()} disabled={resend.isPending}>
                      {resend.isPending ? 'Sending…' : 'Confirm re-send'}
                    </button>
                    <button
                      className="secondary"
                      onClick={() => set_confirming(false)}
                      disabled={resend.isPending}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {resend.error && (
                <p className="error" style={{ marginTop: 12 }}>
                  Re-send failed:{' '}
                  {resend.error instanceof ApiError
                    ? resend.error.reason
                    : String(resend.error)}
                </p>
              )}
              {resend.data && (
                <div style={{ marginTop: 12 }}>
                  <DataTable
                    rows={resend.data.resent}
                    columns={[
                      { header: 'Channel', cell: (r) => r.channel },
                      { header: 'Audience', cell: (r) => r.audience },
                      { header: 'To', cell: (r) => <span style={{ fontSize: 13 }}>{(r.to ?? []).join(', ')}</span> },
                      {
                        header: 'Result',
                        cell: (r) => (
                          <Badge color={message_status_color(r.status)}>{r.status}</Badge>
                        ),
                      },
                      {
                        header: 'Error',
                        cell: (r) =>
                          r.error ? <span className="error">{r.error}</span> : <span className="muted">—</span>,
                      },
                    ]}
                    empty_message="Nothing was re-sent."
                  />
                </div>
              )}
            </Section>

            {b?.status !== 'CANCELLED' && (
              <Section title="Cancel appointment">
                {!cancel_confirming ? (
                  <button
                    className="secondary"
                    onClick={() => set_cancel_confirming(true)}
                    disabled={cancel.isPending}
                  >
                    Cancel appointment
                  </button>
                ) : (
                  <div className="card" style={{ padding: 16, background: 'var(--green-50)' }}>
                    <p style={{ marginTop: 0 }}>
                      Cancel this appointment? This frees the time slot. The customer is not
                      notified automatically.
                    </p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                        {cancel.isPending ? 'Cancelling…' : 'Confirm cancel'}
                      </button>
                      <button
                        className="secondary"
                        onClick={() => set_cancel_confirming(false)}
                        disabled={cancel.isPending}
                      >
                        Keep it
                      </button>
                    </div>
                  </div>
                )}
                {cancel.error && (
                  <p className="error" style={{ marginTop: 12 }}>
                    Cancel failed:{' '}
                    {cancel.error instanceof ApiError ? cancel.error.reason : String(cancel.error)}
                  </p>
                )}
                {cancel.data && (
                  <p className="muted" style={{ marginTop: 12 }}>
                    {cancel.data.already_cancelled
                      ? 'This appointment was already cancelled.'
                      : 'Appointment cancelled — the slot is now free.'}
                  </p>
                )}
              </Section>
            )}
          </>
        )}
      </div>
    </>
  );
}
